import React, { useContext, useState } from "react";
import { LatLng } from "leaflet";

import useForm from "../../hooks/useForm";

import Block from "../Block";
import Input from "../Input";
import Icon from "../Icon";
import { Button } from "../Button";
import { get } from "../../utilities/localStorage";
import localStorageKeys from "../../constants/localStorageKeys";

import styles from "./AddForm.module.css";
import AuthContext from "../../context/AuthContext";
import Select from "../Select";

type Props = {
  userName?: string | null;
  userEmail?: string | null;
  location?: LatLng;
  categories?: { value: string; text: string }[];
  onGoBack: () => void;
  onCompleted: () => void;
};

const AddForm: React.FC<Props> = ({
  userName,
  userEmail,
  location,
  categories,
  onGoBack,
  onCompleted,
}) => {
  const { setUser } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState<File | null>(null);

  const userId = get(localStorageKeys.USER_ID);
  const hasLocaton = location !== undefined;

  const handleSetImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files) {
      setImage(files[0]);
    }
  };

  const form = useForm({
    initialValues: {
      name: "",
      userName: userName || "",
      userEmail: userEmail || "",
    },
    validation: {
      name: {
        regex: /.*\S.*/,
        allowEmpty: false,
        message: "Påkrevd felt",
      },
      description: {
        regex: /.*\S.*/,
        allowEmpty: false,
        message: "Påkrevd felt",
      },
      categoryId: {
        regex: /.*\S.*/,
        allowEmpty: false,
        message: "Påkrevd felt",
      },
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);

      let imageUrl = null;

      // If image is set, upload this first
      if (image) {
        try {
          const data = new FormData();
          data.append("file", image);
          data.append(
            "upload_preset",
            process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || ""
          );

          const postResponse = await fetch(
            process.env.REACT_APP_CLOUDINARY_UPLOAD_URL || "",
            {
              method: "POST",
              body: data,
            }
          );

          if (!postResponse.ok) {
            alert(
              "Kunne ikke laste opp bilde. Det er muligens for stort? Maksimal størrelse er 10MB"
            );
            setIsSubmitting(false);
            return;
          }

          const imageSrc = await postResponse.json();
          imageUrl = imageSrc.url;
        } catch (error) {
          setIsSubmitting(false);
          alert(error);
        }
      }

      const variables = {
        userId,
        name: String(values.name),
        description: String(values.description),
        userName: String(values.userName),
        userEmail: String(values.userEmail),
        category: String(values.categoryId),
        location,
      } as { [key: string]: string | LatLng };

      // Add image if exists
      if (imageUrl) {
        variables.imageUrl = String(imageUrl);
      }

      // Post to sanity proxy api
      try {
        const response = await fetch("/api/pin", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(variables),
        });

        if (!response.ok) {
          throw new Error("Kunne ikke legge til innspel");
        }

        // Persist username and email
        if (values.userName) {
          setUser(
            String(values.userName),
            values.userEmail ? String(values.userEmail) : undefined
          );
        }

        onCompleted();
      } catch (error) {
        alert(error);
      }

      setIsSubmitting(false);
    },
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button className={styles.button} onClick={onGoBack}>
          <Icon type="arrow" size="small" mirrored />
        </button>

        <div className={styles["header-inner"]}>
          <h3 className={styles.title}>Ditt innspel</h3>
        </div>

        <div className={styles.button} style={{ opacity: 0 }}></div>
      </div>

      <form onSubmit={form.handleSubmit}>
        <Block top={4}>
          <Input
            type="text"
            name="name"
            placeholder="Gje kulturminnet eit namn"
            value={form.values.name}
            error={form.errors.name}
            onChange={form.handleChange}
            disabled={isSubmitting}
          />
        </Block>
        <Block top={2}>
          <Input
            type="textarea"
            name="description"
            placeholder="Beskriv kulturminnet og kvifor det bør bli ein del av kulturminneplanen"
            value={form.values.description}
            error={form.errors.description}
            onChange={form.handleChange}
            disabled={isSubmitting}
            multiline
          />
        </Block>
        <Block top={2}>
          <Select
            name="categoryId"
            placeholder="Vel kategori"
            options={categories}
            selected={form.values.categoryId}
            error={form.errors.categoryId}
            onChange={form.handleChange}
            disabled={isSubmitting}
          />
        </Block>

        <Block top={2}>
          <>
            {!image && (
              <label className={styles["upload-label"]}>
                📷
                <span>Legg til bilde</span>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleSetImage}
                />
              </label>
            )}
          </>
          <>
            {image && (
              <>
                <div className={styles["image-preview"]}>
                  <img alt="forhåndsvisning" src={URL.createObjectURL(image)} />
                </div>
                <button
                  className={styles["image-remove"]}
                  onClick={() => setImage(null)}
                >
                  Fjern bilde
                </button>
              </>
            )}
          </>
        </Block>

        <Block top={2}>
          <Input
            type="text"
            name="userName"
            placeholder="Ditt navn"
            value={form.values.userName}
            error={form.errors.userName}
            onChange={form.handleChange}
            disabled={isSubmitting}
          />
        </Block>
        <Block top={2}>
          <Input
            type="text"
            name="userEmail"
            placeholder="Din e-postadresse"
            value={form.values.userEmail}
            error={form.errors.userEmail}
            onChange={form.handleChange}
            disabled={isSubmitting}
          />
        </Block>

        <Block top={4}>
          <Button
            type="submit"
            width="full"
            loading={isSubmitting}
            disabled={!hasLocaton}
          >
            {!hasLocaton
              ? "Velg plassering først"
              : isSubmitting
              ? "Legger til.."
              : "Legg til"}
          </Button>
        </Block>
      </form>
    </div>
  );
};

export default AddForm;
